import { getReportFooterText } from '../reportFooter';
export interface InventoryAwarenessItem {
  lin?: string | null;
  nsn?: string | null;
  nomenclature: string;
  onHandQty?: number | null;
  minQty?: number | null;
  maxQty?: number | null;
  qtyOnOrder?: number | null;
  site?: string | null;
  location?: string | null;
  sublocation?: string | null;
}

export interface InventoryAwarenessTemplateOptions {
  generatedAt: Date;
  header?: {
    assetCategory?: string | null;
  };
  outOfStock: InventoryAwarenessItem[];
  exceedingMax: InventoryAwarenessItem[];
  belowMin: InventoryAwarenessItem[];
  withinNormal: InventoryAwarenessItem[];
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeText = (value: string | null | undefined): string => {
  const trimmed = value?.toString().trim();
  if (!trimmed) {
    return '—';
  }
  return escapeHtml(trimmed);
};

const formatNumber = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

const formatDateTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(value);

const buildSectionTable = (items: InventoryAwarenessItem[]): string => {
  if (items.length === 0) {
    return `
      <tr>
        <td colspan="10" class="empty-row">
          No items in this category.
        </td>
      </tr>
    `;
  }

  return items
    .map(
      (item) => `
        <tr>
          <td>${sanitizeText(item.lin)}</td>
          <td>${sanitizeText(item.nsn)}</td>
          <td>${sanitizeText(item.nomenclature)}</td>
          <td class="numeric">${formatNumber(item.onHandQty ?? null)}</td>
          <td class="numeric">${formatNumber(item.minQty ?? null)}</td>
          <td class="numeric">${formatNumber(item.maxQty ?? null)}</td>
          <td class="numeric">${formatNumber(item.qtyOnOrder ?? null)}</td>
          <td>${sanitizeText(item.site)}</td>
          <td>${sanitizeText(item.location)}</td>
          <td>${sanitizeText(item.sublocation)}</td>
        </tr>
      `,
    )
    .join('');
};

export const buildInventoryAwarenessReportHtml = ({
  generatedAt,
  header,
  outOfStock,
  exceedingMax,
  belowMin,
  withinNormal,
}: InventoryAwarenessTemplateOptions): string => {
  const generatedLabel = formatDateTime(generatedAt);
  const selectedCategory =
    header?.assetCategory && header.assetCategory.trim() ? header.assetCategory.trim() : null;
  const assetCategoryLabel = selectedCategory ? sanitizeText(selectedCategory) : '—';

  const outOfStockRows = buildSectionTable(outOfStock);
  const exceedingMaxRows = buildSectionTable(exceedingMax);
  const belowMinRows = buildSectionTable(belowMin);
  const withinRows = buildSectionTable(withinNormal);

  const section = (title: string, rows: string) => `
    <section class="section-block">
      <div class="section-title">${escapeHtml(title)}</div>
      <table data-sheet-name="${escapeHtml(title)}">
        <thead>
          <tr>
            <th>LIN</th>
            <th>NSN</th>
            <th>Nomenclature</th>
            <th class="numeric">On Hand Quantity</th>
            <th class="numeric">Min Qty</th>
            <th class="numeric">Max Qty</th>
            <th class="numeric">Qty on Order</th>
            <th>Site</th>
            <th>Location</th>
            <th>Sublocation</th>
          </tr>
        </thead>
        <tbody>
          ${rows}
        </tbody>
      </table>
    </section>
  `;

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Inventory Awareness Report</title>
    <style>
      :root {
        color-scheme: light;
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 16px;
        font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        color: #15263c;
        background: #ffffff;
      }

      header {
        margin-bottom: 16px;
      }

      .title {
        font-size: 24px;
        font-weight: 700;
        margin: 0;
        text-align: center;
      }

      .generated {
        margin-top: 4px;
        font-size: 12px;
        color: #5c6f85;
        text-align: right;
      }

      .filters-row {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        margin-top: 12px;
        margin-bottom: 12px;
      }

      .filter-block {
        min-width: 240px;
      }

      .filter-label {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #5c6f85;
        margin-bottom: 2px;
      }

      .filter-value {
        font-size: 14px;
        color: #15263c;
      }

      .section-block {
        margin-top: 16px;
      }

      .section-title {
        font-size: 16px;
        font-weight: 600;
        padding: 6px 10px;
        background: #e9f1ff;
        border: 1px solid #d7e3f4;
        border-bottom: none;
        page-break-after: avoid;
      }

      table {
        width: 100%;
        table-layout: fixed;
        border-collapse: collapse;
        border: 1px solid #d7e3f4;
      }

      table thead th:nth-child(1) { width: 7%; }   /* LIN */
      table thead th:nth-child(2) { width: 12%; }  /* NSN */
      table thead th:nth-child(3) { width: 18%; }  /* Nomenclature */
      table thead th:nth-child(4) { width: 8%; }   /* On Hand Quantity */
      table thead th:nth-child(5) { width: 7%; }   /* Min Qty */
      table thead th:nth-child(6) { width: 7%; }   /* Max Qty */
      table thead th:nth-child(7) { width: 8%; }   /* Qty on Order */
      table thead th:nth-child(8) { width: 8%; }   /* Site */
      table thead th:nth-child(9) { width: 11%; }  /* Location */
      table thead th:nth-child(10) { width: 14%; } /* Sublocation */

      thead th {
        background: #e9f1ff;
        text-align: left;
        font-size: 11px;
        letter-spacing: 0.02em;
        text-transform: uppercase;
        line-height: 1.2;
        white-space: normal;
        word-break: break-word;
        overflow-wrap: anywhere;
        padding: 8px 6px;
        color: #4a5c72;
        border-bottom: 1px solid #d7e3f4;
      }

      tbody td {
        padding: 8px 10px;
        border-bottom: 1px solid #edf3fc;
        font-size: 14px;
        color: #1f2f45;
      }

      tbody tr:last-child td {
        border-bottom: none;
      }

      tbody tr:nth-child(even) td {
        background: #f9fbff;
      }

      .numeric {
        text-align: center;
        font-variant-numeric: tabular-nums;
      }

      .empty-row {
        text-align: center;
        font-style: italic;
        color: #6b7c92;
        padding: 24px 12px;
      }

      footer {
        margin-top: 24px;
        font-size: 12px;
        color: #5c6f85;
        text-align: center;
      }

      @media print {
        @page {
          margin: 8mm;
        }

        body {
          margin: 0;
        }
      }
    </style>
  </head>
  <body>
    <header>
      <h1 class="title">Inventory Awareness Report</h1>
      <div class="filters-row">
        <div class="filter-block">
          <div class="filter-label">Asset Category</div>
          <div class="filter-value">${assetCategoryLabel}</div>
        </div>
      </div>
      <div class="generated">Generated ${escapeHtml(generatedLabel)}</div>
    </header>

    ${!selectedCategory || selectedCategory === 'Out of Stock' ? section('Out of Stock', outOfStockRows) : ''}
    ${!selectedCategory || selectedCategory === 'Exceeding Max Qty Level' ? section('Exceeding Max Qty Level', exceedingMaxRows) : ''}
    ${!selectedCategory || selectedCategory === 'Below Min Qty Level' ? section('Below Min Qty Level', belowMinRows) : ''}
    ${!selectedCategory || selectedCategory === 'Within Normal Range' ? section('Within Normal Range', withinRows) : ''}

    <footer>
      Generated ${escapeHtml(generatedLabel)} · ${getReportFooterText()}
    </footer>
  </body>
</html>`;
};
